<template>
  <div v-if="loaded">
    <!-- HEADER -->
    <b-row class="px-3">
      <b-col class="header d-flex align-items-center">
        <span
          v-if="entityIcon"
          :class="entityIcon"
        />
        <span
          class="title label-dark mx-2"
          qa="global-search-single-result-found-item-span"
        >{{ foundItem }}</span>
        <i
          v-if="currentLaboratory.labId !== laboratoryId"
          v-tooltip="`${$t('global.elementLocatedIn')} ${currentLaboratory.labName}`"
          qa="global-search-single-result-element-location-i"
          aria-hidden="true"
          class="fas fa-map-marker-alt alt-color mr-2"
        />
        <b-badge
          v-if="entityType === IDENTIFIER_SCOPES.REQUEST && entityValue.automaticallyCreated"
          v-tooltip="$t('global.automaticCreation')"
          qa="global-search-single-result-automatic-creation-span"
          pill
          class="ml-1 icon-badge indicator-bubble d-flex align-items-center justify-content-center"
        >
          <i
            class="far fa-magic"
            aria-hidden="true"
          />
        </b-badge>
        <b-badge
          v-if="entityValue.caseOfReference"
          v-tooltip="$t('registry.btn.consultation')"
          qa="global-search-single-result-case-of-reference-span"
          pill
          class="mx-1 icon-badge indicator-bubble d-flex align-items-center justify-content-center"
        >
          <i
            class="far fa-glasses"
            aria-hidden="true"
          />
        </b-badge>
        <priority-dot
          v-if="priority !== undefined"
          class="ml-2"
          qa="global-search-single-result-priority-i"
          :priority-level="priority"
        />
        <vtBubble
          :number="entityValue.incidentsCount"
          :type="true"
          :tooltip="' '+$t('grid.detail.hasAlarms')"
          qa="global-search-single-result-incidents-div"
          class="mr-1"
        />
        <vtBubble
          class="mr-1"
          :number="entityValue.attachmentsCount"
          :type="false"
          :tooltip="' '+$tc('global.file',2)"
          qa="global-search-single-result-attachments-div"
        />
        <vtBubble
          v-if="entityValue.complementaryCasesCount"
          class="mr-1"
          :number="entityValue.complementaryCasesCount"
          :info="true"
          :tooltip="' '+$t('globalLocator.labels.complementaries')"
          qa="global-search-single-result-complementaries-div"
        />
        <translated-entity-badge
          class="state-badge auto-width mx-1"
          :value="entityValue.status"
          :values="states"
          qa="global-search-single-result-status-span"
        />
        <div
          v-if="entityValue.isDepleted"
          class="mx-3"
          qa="global-search-single-result-is-depleted-div"
        >
          <span class="depleted-text"><i
            class="far fa-empty-set mr-1"
            aria-hidden="true"
          />{{
            $t('global.depletedSample')
          }}</span>
        </div>
        <!-- TODO: Solo aparece en caso de que haya intercambio de elementos, en elementos que se puedan intercambiar, muestra el centro donde se encuentra actualmente el elemento -->
        <!--        <div class="mx-3">
          <i
            v-tooltip="$t('exchanges.tracking.currentCenter')"
            class="main far fa-map-marker-alt mr-2"
           aria-hidden="true"/>
          <span class="main">anatomical pathology</span>
        </div>-->
      </b-col>
    </b-row>
    <!-- CONTENT -->
    <b-row class="mt-3 px-3">
      <b-col>
        <b-tabs
          v-model="selectedTabIndex"
          class="vt-tabs--vertical h-100"
          pills
          card
          vertical
        >
          <b-tab class="px-0">
            <template #title>
              <i
                class="main fas fa-info"
                qa="global-search-single-result-info-tab"
                aria-hidden="true"
              />
            </template>
            <div class="px-3 py-2 section-content">
              <information-section
                :entity-type="entityType"
                :entity-value="entityValue"
              />
            </div>
          </b-tab>
          <b-tab
            class="px-0"
            @click="$refs.trackingSection.loadTrackingElements()"
          >
            <template #title>
              <i
                class="main fal fa-map-marker-alt"
                qa="global-search-single-result-tracking-tab"
                aria-hidden="true"
              />
            </template>
            <div class="px-3 py-2 section-content">
              <tracking-section
                ref="trackingSection"
                :states="states"
                :laboratory-id="laboratoryId"
                :entity-type="entityType"
                :entity-value="entityValue"
              />
            </div>
          </b-tab>
          <b-tab @click="$refs.patientSection.loadPatient()">
            <template #title>
              <i
                class="main fal fa-user-circle"
                qa="global-search-single-result-patient-tab"
                aria-hidden="true"
              />
            </template>
            <div class="py-2 section-content">
              <patient-section
                v-if="patientId"
                ref="patientSection"
                :patient-id="patientId"
              />
            </div>
          </b-tab>
          <b-tab v-if="caseStudy && entityType !== IDENTIFIER_SCOPES.CASE">
            <template #title>
              <i
                class="main fal fa-chart-network"
                qa="global-search-single-result-case-tab"
                aria-hidden="true"
              />
            </template>
            <div class="py-2 section-content">
              <case-section :case-study="caseStudy" />
            </div>
          </b-tab>
          <b-tab
            v-if="entityType !== IDENTIFIER_SCOPES.REQUEST"
            @click="$refs.sampleSection.loadSamples()"
          >
            <template #title>
              <i
                class="main fal fa-prescription-bottle"
                qa="global-search-single-result-sample-tab"
                aria-hidden="true"
              />
              <div class="section-indicator">
                <vtBubble
                  class="mr-1"
                  :number="caseStudy && caseStudy.samplesCount"
                  :indicator="true"
                />
              </div>
            </template>
            <div class="py-2 section-content">
              <sample-section
                ref="sampleSection"
                :found-item="foundItem"
                :laboratory-id="laboratoryId"
                :case-id="caseStudy.id"
              />
            </div>
          </b-tab>
          <b-tab
            v-if="entityType !== IDENTIFIER_SCOPES.REQUEST"
            @click="$refs.portionSection.loadPortions()"
          >
            <template #title>
              <i
                class="main icon-block"
                qa="global-search-single-result-portion-tab"
                aria-hidden="true"
              />
              <div class="section-indicator">
                <vtBubble
                  class="mr-1"
                  :number="caseStudy && caseStudy.portionsCount"
                  :indicator="true"
                />
              </div>
            </template>
            <div class="py-2 section-content">
              <portion-section
                ref="portionSection"
                :found-item="foundItem"
                :laboratory-id="laboratoryId"
                :case-id="caseStudy.id"
              />
            </div>
          </b-tab>
          <b-tab
            v-if="entityType !== IDENTIFIER_SCOPES.REQUEST"
            @click="$refs.subportionSection.loadSubportions()"
          >
            <template #title>
              <i
                class="main icon-porta"
                qa="global-search-single-result-subportion-tab"
                aria-hidden="true"
              />
              <div class="section-indicator">
                <vtBubble
                  class="mr-1"
                  :number="caseStudy && caseStudy.subportionsCount"
                  :indicator="true"
                />
              </div>
            </template>
            <div class="py-2 section-content">
              <subportion-section
                ref="subportionSection"
                :found-item="foundItem"
                :laboratory-id="laboratoryId"
                :case-id="caseStudy.id"
              />
            </div>
          </b-tab>
        </b-tabs>
      </b-col>
    </b-row>
  </div>
</template>

<script>

import PriorityDot from '@/modules/clinicalModule/components/StatusRequestPriorityDot'
import VtBubble from '@/components/common/VtBubble'
import TranslatedEntityBadge from '@/components/common/TranslatedEntityBadge'
import InformationSection from './info/InformationSection'
import TrackingSection from './TrackingSection'
import PatientSection from './PatientSection'
import CaseSection from './CaseSection'
import SampleSection from './sample/SampleSection'
import PortionSection from './portion/PortionSection'
import SubportionSection from './subportion/SubportionSection'
import { requestState } from '@/api/resources/entityState/request-state'
import { caseState } from '@/api/resources/entityState/case-state'
import { sampleState } from '@/api/resources/entityState/sample-state'
import { portionState } from '@/api/resources/entityState/portion-state'
import { subportionState } from '@/api/resources/entityState/subportion-state'
import { IDENTIFIER_SCOPES } from '@/api/resources/identifierScopesResource'

const TABS = {
    INFO: 0
}

export default {
    name: 'GlobalSearchSingleResult',
    components: {
        PriorityDot,
        VtBubble,
        TranslatedEntityBadge,
        InformationSection,
        TrackingSection,
        PatientSection,
        CaseSection,
        SampleSection,
        PortionSection,
        SubportionSection
    },
    props: {
        entityType: { type: String, required: true },
        entityValue: { type: Object, required: true },
        laboratoryId: { type: Number, required: true },
        foundItem: String
    },
    data() {
        return {
            selectedTabIndex: 0,
            loaded: false
        }
    },
    computed: {
        IDENTIFIER_SCOPES() {
            return IDENTIFIER_SCOPES
        },
        currentLaboratory() {
            if (this.entityValue.currentLaboratory) {
                return this.entityValue.currentLaboratory
            } else {
                return this.entityValue.laboratory
            }
        },
        caseStudy() {
            switch (this.entityType) {
            case IDENTIFIER_SCOPES.CASE:
                return this.entityValue
            case IDENTIFIER_SCOPES.SAMPLE:
                return this.entityValue.caseStudy
            case IDENTIFIER_SCOPES.PORTION:
                return this.entityValue.sample.caseStudy
            case IDENTIFIER_SCOPES.SUBPORTION:
                return this.entityValue.portion.sample.caseStudy
            default:
                return undefined
            }
        },
        patientId() {
            if (this.caseStudy) {
                return this.caseStudy.patientId
            } else if (this.entityType === IDENTIFIER_SCOPES.REQUEST) {
                return this.entityValue.patient.id
            } else {
                return NaN
            }
        },
        priority() {
            if (this.entityValue && this.entityValue.priority) {
                return this.entityValue.priority.level
            }
            return undefined
        },
        entityIcon() {
            switch (this.entityType) {
            case IDENTIFIER_SCOPES.CASE: return 'main fal fa-chart-network'
            case IDENTIFIER_SCOPES.SAMPLE: return 'main fal fa-prescription-bottle'
            case IDENTIFIER_SCOPES.PORTION: return 'main icon-block'
            case IDENTIFIER_SCOPES.SUBPORTION: return 'main icon-porta'
            case IDENTIFIER_SCOPES.REQUEST: return 'main fal fa-file-medical'
            default:
                console.error('unknown entity type', this.entityType)
                return ''
            }
        },
        states() {
            switch (this.entityType) {
            case IDENTIFIER_SCOPES.CASE:
                return caseState.states
            case IDENTIFIER_SCOPES.SAMPLE:
                return sampleState.states
            case IDENTIFIER_SCOPES.PORTION:
                return portionState.states
            case IDENTIFIER_SCOPES.SUBPORTION:
                return subportionState.states
            case IDENTIFIER_SCOPES.REQUEST:
                return requestState.states
            default:
                console.error('unknown entity type', this.entityType)
                return null
            }
        }
    },
    watch: {
        entityValue() {
            this.selectedTabIndex = TABS.INFO
        }
    },
    async created() {
        const [err] = await this.toCall(subportionState.loadStates())
        if (err) {
            this.showErrorModalGlobal()
            return
        }
        switch (this.entityType) {
        case IDENTIFIER_SCOPES.CASE:
            await caseState.loadStates()
            break
        case IDENTIFIER_SCOPES.SAMPLE:
            await sampleState.loadStates()
            break
        case IDENTIFIER_SCOPES.PORTION:
            await portionState.loadStates()
            break
        case IDENTIFIER_SCOPES.REQUEST:
            await requestState.loadStates()
            break
        }
        this.loaded = true
    }
}
</script>

<style lang="scss">

.global-search-single-result .showtooltip{
  display: block !important;
  cursor: pointer;
}

.global-search-single-result .noshowtooltip{
  display: none !important;
}

.global-search-single-result .section-content .title{
    font-size: font-size(big);
    color: vt-color(primary);
}

.global-search-single-result .subtitle{
    color: vt-color(dark-text);
}

.global-search-single-result .no-gutters > .col {
  height: 100%;
}

.global-search-single-result .modal-dialog {
  max-width: 55% !important;
}

.global-search-single-result .vt-tabs .nav-tabs .nav-link,
.vt-tabs--small .nav-tabs .nav-link {
  font-size: font-size(very-small) !important;
}

.global-search-single-result .nav-pills {
  padding: 0 !important;
  background: vt-color(light-primary);
  height: 100% !important;
}

.global-search-single-result .nav-link.active {
  background: vt-color(white-light);
  color: vt-color(primary);
  position: relative;
}

.global-search-single-result .nav-link.active .section-icon {
  opacity: 1;
}

.global-search-single-result .nav-link.active::before {
  content: "";
  position: absolute;
  width: 5px;
  height: 100%;
  background: vt-color(primary);
  left: 0;
}

.global-search-single-result .inside-tab .nav-link.active::before {
  content: "";
  background: transparent;
}

.vt-tabs.inside-tab .nav-tabs .nav-link, .vt-tabs--small .nav-tabs .nav-link {
  border-bottom-color: transparent;
}

.global-search-single-result .nav-link {
  border-radius: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  border-bottom: 2px solid vt-color(white-light);
  width: 50px;
}

.global-search-single-result .section-content {
  height: calc(100vh - 380px) !important;
  overflow-y: auto;
  overflow-x: hidden;
}

.global-search-single-result .tabs.row {
  border: 1px solid rgba(0, 0, 0, 0.125);
  height: 100%;
  position: relative;
}

.global-search-single-result .ag-root-wrapper-body.ag-layout-normal{
  padding: 0 !important;
}

@media (max-width: 1300px) {
  .global-search-single-result .modal-dialog {
    max-width: 80% !important;
  }

  .global-search-single-result .section-content .title {
    font-size: font-size(normal);
    color: vt-color(primary);
  }
}
@media only screen and (max-width: 1366px) and (min-width: 1360px) {
  .global-search-single-result .modal-dialog {
    max-width: 80% !important;
  }

  .global-search-single-result .section-content {
  height: calc(100vh - 240px) !important;
}
}
</style>

<style lang="scss" scoped>
.header i,
.header .title {
  font-size: font-size(big);
}

.header .title {
  color: vt-color(dark-text);
  font-weight: bold;
}
.section-indicator{
  position: absolute;
  width: auto;
  left: 23px;
  margin-top: 5px;
}
</style>
